/**
 * Pure URL-mapping functions for image variants.
 * No server-only dependencies — safe for client components.
 */

export const VARIANT_WIDTHS = [64, 256, 640, 1200] as const;
export type VariantWidth = (typeof VARIANT_WIDTHS)[number];

const R2_HOST = "img.finda.show";

/**
 * Map a requested display width to the nearest pre-generated variant URL.
 * Falls back to original URL if width exceeds all variants.
 */
export function getVariantUrl(originalUrl: string, displayWidth: number): string {
  if (!originalUrl.includes(R2_HOST)) return originalUrl;
  // Already a variant URL
  if (originalUrl.match(/_\d+w\.webp$/)) return originalUrl;

  const matchedWidth = VARIANT_WIDTHS.find((w) => w >= displayWidth);
  if (!matchedWidth) return originalUrl;

  try {
    const url = new URL(originalUrl);
    const pathWithoutExt = url.pathname.replace(/\.[^.]+$/, "");
    return `https://${R2_HOST}${pathWithoutExt}_${matchedWidth}w.webp`;
  } catch {
    return originalUrl;
  }
}

/**
 * Get all variant R2 paths for a given original path (for deletion).
 */
export function getVariantPaths(originalPath: string): string[] {
  const pathWithoutExt = originalPath.replace(/\.[^.]+$/, "");
  return VARIANT_WIDTHS.map((w) => `${pathWithoutExt}_${w}w.webp`);
}
